// The SPA half of the indexing policy. index.html ships a static
// noindex, nofollow meta for every route. For the marketing routes the
// server already sends the right tag on first load
// (functions/_lib/marketing.ts), because Google won't render a page whose
// initial HTML says noindex. This keeps the tag right after client-side
// route changes (components/RobotsMeta.tsx), e.g. landing → /home.
//
// Mirrors the indexing guard (functions/_lib/blog/robots.ts): the same
// MARKETING_PATHS, exact match, and only on the prod hostname, so
// dev.wydm.app and *.pages.dev never flip. tests/blog/guard.test.ts pins
// the two lists together.

export const PROD_HOSTNAME = 'wydm.app';

export const MARKETING_PATHS: readonly string[] = ['/', '/pricing', '/terms', '/privacy', '/agents'];

export function robotsMetaContent(hostname: string, pathname: string): string {
  return hostname === PROD_HOSTNAME && MARKETING_PATHS.includes(pathname)
    ? 'index, follow'
    : 'noindex, nofollow';
}

/** index.html's title and description, for every non-marketing route. */
export const DEFAULT_TITLE = 'Would you date me?';
export const DEFAULT_DESCRIPTION =
  'A dating app built around a shareable profile card. Post it and let it spread through your circle to people who already have a reason to say yes.';

/**
 * Each marketing route's title and description. Mirrors MARKETING_PAGES in
 * functions/_lib/marketing.ts, which sends them on first load;
 * tests/marketing.test.ts pins the two together.
 */
export const MARKETING_PAGES: Readonly<Record<string, { title: string; description: string }>> = {
  '/': {
    title: 'Would You Date Me? · Your next date is a few friends away',
    description:
      'A dating app built around a shareable profile card. Post it and let it spread through your circle to people who already have a reason to say yes.',
  },
  '/pricing': {
    title: 'Pricing: Free, Premium and Ultra plans · Would You Date Me?',
    description:
      'Start free: publish your dating card and see who says yes. Premium unlocks unlimited messages and the full app; Ultra adds the AI agent platform.',
  },
  '/agents': {
    title: 'AI dating agents: let an agent run your profile · Would You Date Me?',
    description:
      'Point an AI agent at WYDM and it builds your dating profile and handles the day-to-day through a scoped API and MCP server. You just show up for the dates.',
  },
  '/terms': {
    title: 'Terms & Conditions · Would You Date Me?',
    description: 'The terms that govern your use of Would You Date Me? (WYDM), the dating app built around a shareable profile card.',
  },
  '/privacy': {
    title: 'Privacy Policy · Would You Date Me?',
    description: 'How Would You Date Me? (WYDM) collects, uses and protects your personal data, and the choices you have over it.',
  },
};

export interface PageHead {
  title: string;
  description: string;
  /** Absolute canonical URL, or null for routes that have none. */
  canonical: string | null;
}

export function pageHead(origin: string, pathname: string): PageHead {
  const page = MARKETING_PAGES[pathname];
  if (!page) return { title: DEFAULT_TITLE, description: DEFAULT_DESCRIPTION, canonical: null };
  return { ...page, canonical: `${origin}${pathname}` };
}
